import { Money } from '@dintero/money';
import { isCryptoCode, getCryptoDecimals } from 'shared/src/cryptoUtils';

import { formatRenderedCurrency } from 'utils/formatters/formatCurrency';

import type { NormalizedFx } from 'hooks/useFxRate';

import {
  FX_CALCULATED_AMOUNT,
  MAX_AMOUNT_VALUE,
  TRANSLATIONS
} from './constants';

export function ExchangeRateDisplay({
  exchangeRateData,
  amountToConvert = 1,
  max = MAX_AMOUNT_VALUE
}: {
  exchangeRateData: NormalizedFx | undefined;
  amountToConvert: number;
  max?: number;
}) {
  if (!exchangeRateData) {
    return null;
  }
  const { buyCurrency, payCurrency, exchangeRate, reciprocalExchangeRate } =
    exchangeRateData;

  const isValidRate = !isNaN(exchangeRate) && reciprocalExchangeRate !== 0;

  if (!buyCurrency || !payCurrency || !isValidRate) {
    return <p>{TRANSLATIONS.ERROR_LOADING}</p>;
  }

  const price = Money.fromPrice(exchangeRate, buyCurrency).toNumber();
  const reciprocalPrice = Money.fromPrice(
    reciprocalExchangeRate,
    buyCurrency
  ).toNumber();

  const formattedExchangeRate = `1 ${buyCurrency} = ${price} ${payCurrency}`;
  const reciprocalFormattedExchangeRate = `1 ${payCurrency} = ${reciprocalPrice} ${buyCurrency}`;

  if (amountToConvert >= max) {
    return (
      <div className="flex flex-col">
        <p data-testid={FX_CALCULATED_AMOUNT}>Amount too big</p>
        <Rate price={reciprocalFormattedExchangeRate} />
        <Rate price={formattedExchangeRate} />
      </div>
    );
  }

  // Specifies merge options for Money
  // If crypto, get specific decimals, else return undefined
  // this.resetDecimals on Money simply calls this.merge({ decimal: undefined })
  // If decimals is undefined, Money looks up the code and uses the specified decimals from the ISO code
  // Hence, we only need to specify the decimals for crypto codes
  const mergeOptions = {
    decimals: isCryptoCode(buyCurrency) ? getCryptoDecimals(buyCurrency) : 2
  };

  const calculatedMoney = Money.fromPrice(reciprocalExchangeRate, buyCurrency)
    .multiply(amountToConvert)
    .merge(mergeOptions)
    .toNumber();

  const formattedCalculatedAmount = `${formatRenderedCurrency(amountToConvert, payCurrency)} = ${formatRenderedCurrency(calculatedMoney, buyCurrency)}`;

  return (
    <div className="flex flex-col">
      <p data-testid={FX_CALCULATED_AMOUNT}>{formattedCalculatedAmount}</p>
      <Rate price={reciprocalFormattedExchangeRate} />
      <Rate price={formattedExchangeRate} />
    </div>
  );
}

function Rate({ price }: { price: string }) {
  return <p className="text-xs font-light text-gray-500">{price}</p>;
}
